<template>
  <v-dialog v-model="editDialog" class="the-app-dialogs" persistent max-width="900px">
    <v-card>
      <form @submit.prevent="validateBeforeSubmit($event)">
        <v-layout row class="no-select" style="background-color:#09132f">
          <h3 id="dialog-title" class="text-uppercase px-3 pt-3" style="text-xs-center letter-spacing:2px">
            <v-layout align-center>
              <v-icon light x-large class="rgba(0,0,0,0.3)--text pb-1">fa-dolly</v-icon>
              <span class="px-3">{{ $t('Edit Production information') }}</span>
            </v-layout>
          </h3>
          <v-spacer />
          <v-card-actions>
            <v-btn fab icon dark @click="$emit('hidemodal')">
              <v-icon>close</v-icon>
            </v-btn>
          </v-card-actions>
        </v-layout>

        <v-card-title class="grey lighten-3">
          <v-layout column>
            <v-flex xs12 class="headline no-select pb-1">
              <v-layout justify-end></v-layout>
            </v-flex>

            <v-layout row wrap>
              <v-flex xs11 :class="{ xs12: !hasPermissions(['users_store']) }" pa-2>
                <v-autocomplete
                  :items="fullStaffList"
                  v-model="updateForm.staff_id_fk"
                  :label="`${$t('Staff')} *`"
                  box
                  multiple
                  chips
                  deletable-chips
                  hide-details
                  :error="errors.has('Staff')"
                  background-color="white"
                  v-validate="'required'"
                  data-vv-name="Staff"
                  :menu-props="{ transition: 'slide-y-transition' }"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index < 3">
                      <span>{{ item.text }}</span>
                    </v-chip>
                    <span v-if="index === 3" class="grey--text caption"
                      >(+{{ updateForm.staff_id_fk.length - 3 }} others)</span
                    >
                  </template>
                </v-autocomplete>
              </v-flex>

              <v-flex xs1 pt-3 v-if="hasPermissions(['users_store'])">
                <v-tooltip top>
                  <v-btn label @click="$emit('add-staff')" slot="activator" icon fab small color="green">
                    <v-icon color="white" size="25">add</v-icon>
                  </v-btn>
                  <span>{{ $t('Add new staff') }}</span>
                </v-tooltip>
              </v-flex>

              <v-flex xs11 :class="{ xs12: !hasPermissions(['factory_store']) }" pa-2>
                <v-autocomplete
                  :items="fullFactoriesList"
                  v-model="updateForm.factory_id_fk"
                  :label="`${$t('Factory')} *`"
                  box
                  hide-details
                  :error="errors.has('factory')"
                  background-color="white"
                  v-validate="'required'"
                  data-vv-name="factory"
                  :menu-props="{ transition: 'slide-y-transition' }"
                />
              </v-flex>

              <v-flex xs1 pt-2 v-if="hasPermissions(['factory_store'])">
                <v-tooltip top>
                  <v-btn label @click="$emit('add-factory')" slot="activator" icon fab small color="green">
                    <v-icon color="white" size="25">add</v-icon>
                  </v-btn>
                  <span>{{ $t('Add new factory') }}</span>
                </v-tooltip>
              </v-flex>

              <v-flex pa-2 xs5 :class="{ xs6: !hasPermissions(['products_store']) }">
                <v-autocomplete
                  :items="fullProductList"
                  v-model="updateForm.product_id_fk"
                  :label="`${$t('Product')} *`"
                  box
                  hide-details
                  :error="errors.has('Product')"
                  background-color="white"
                  v-validate="'required'"
                  data-vv-name="Product"
                  :menu-props="{ transition: 'slide-y-transition' }"
                />
              </v-flex>

              <v-flex xs1 pt-2 v-if="hasPermissions(['products_store'])">
                <v-tooltip top>
                  <v-btn label @click="$emit('add-product')" slot="activator" icon fab small color="green">
                    <v-icon color="white" size="25">add</v-icon>
                  </v-btn>
                  <span>{{ $t('Add new product') }}</span>
                </v-tooltip>
              </v-flex>

              <v-flex pa-2 xs5 :class="{ xs6: !hasPermissions(['mixtures_store']) }">
                <v-autocomplete
                  :items="fullMixtureList"
                  v-model="updateForm.mixture_id_fk"
                  :label="`${$t('Mixture')} *`"
                  box
                  hide-details
                  :error="errors.has('Mixture')"
                  background-color="white"
                  v-validate="'required'"
                  data-vv-name="Mixture"
                  :menu-props="{ transition: 'slide-y-transition' }"
                  @input="getMixtureInfo(updateForm.mixture_id_fk)"
                />
              </v-flex>

              <v-flex xs1 pt-2 v-if="hasPermissions(['mixtures_store'])">
                <v-tooltip top>
                  <v-btn label @click="$emit('add-mixture')" slot="activator" icon fab small color="green">
                    <v-icon color="white" size="25">add</v-icon>
                  </v-btn>
                  <span>{{ $t('Add new mixture') }}</span>
                </v-tooltip>
              </v-flex>

              <v-flex xs3 class="pa-2">
                <v-text-field
                  v-model="updateForm.used_mixture_quantity"
                  :label="`${$t('Used Mixture Quantity')} *`"
                  box
                  hide-details
                  name="used_mixture_quantity"
                  background-color="white"
                  :error="errors.has('Used Mixture Quantity')"
                  v-validate="'required|decimal:3|min_value:0'"
                  data-vv-name="Used Mixture Quantity"
                  data-vv-delay="1000"
                />
              </v-flex>

              <v-flex xs3 class="pa-2">
                <v-text-field
                  v-model="updateForm.production_quantity"
                  :label="`${$t('Production Quantity')} *`"
                  box
                  hide-details
                  name="production_quantity"
                  background-color="white"
                  :error="errors.has('Production Quantity')"
                  v-validate="'required|decimal:3|min_value:0'"
                  data-vv-name="Production Quantity"
                  data-vv-delay="1000"
                />
              </v-flex>

              <v-flex xs3 class="pa-2">
                <v-text-field
                  :value="estimatedQuantity"
                  :label="`${$t('Estimated Product Quantity')} *`"
                  box
                  hide-details
                  readonly
                  name="estimated_product_quantity"
                  background-color="white"
                  :error="errors.has('estimated_product_quantity')"
                  v-validate="'required|decimal:3|min_value:0'"
                  data-vv-name="estimated_product_quantity"
                  data-vv-delay="1000"
                />
              </v-flex>

              <v-flex xs3 class="pa-2">
                <v-text-field
                  :value="damagedQuantity"
                  :label="`${$t('Damaged Product Quantity')} *`"
                  box
                  hide-details
                  readonly
                  name="damaged_product_quantity"
                  background-color="white"
                  :error="errors.has('damaged_product_quantity')"
                  v-validate="'required|decimal:3|min_value:0'"
                  data-vv-name="damaged_product_quantity"
                  data-vv-delay="1000"
                />
              </v-flex>

              <v-flex xs6 class="pa-1">
                <v-autocomplete
                  v-model="updateForm.recycle_raw_material_id_fk"
                  :label="`${$t('Recycle Raw Materials')}`"
                  :items="rawMaterialList"
                  hide-details
                  box
                  :data-vv-name="'recycle_raw_material'"
                  background-color="white"
                  :error="errors.has('recycle_raw_material')"
                />
              </v-flex>
              <v-flex xs6 class="pa-1">
                <v-text-field
                  v-model.number="updateForm.recycle_raw_material_quantity"
                  :label="`${$t('Recycle Quantity')}`"
                  hide-details
                  box
                  name="recycle_raw_material_quantity"
                  background-color="white"
                  :error="errors.has('recycle_raw_material_quantity')"
                  v-validate="'decimal:3|min_value:0'"
                  data-vv-delay="1000"
                />
              </v-flex>

              <v-flex class="pa-2">
                <v-autocomplete
                  :items="shiftTimes"
                  v-model="updateForm.shift_time"
                  :label="`${$t('Shift Time')} *`"
                  box
                  hide-details
                  :error="errors.has('Shift Time')"
                  background-color="white"
                  v-validate="'required'"
                  data-vv-name="Shift Time"
                  :menu-props="{ transition: 'slide-y-transition' }"
                />
              </v-flex>

              <v-flex class="pa-2">
                <v-menu v-model="productionDateMenu" full-width max-width="290" :close-on-content-click="false">
                  <v-text-field
                    slot="activator"
                    :value="updateForm.production_date"
                    :label="`${$t('Production Date')} *`"
                    prepend-inner-icon="event"
                    box
                    readonly
                    hide-details
                    background-color="white"
                    autocomplete="off"
                    data-vv-name="Production Date"
                    :error="errors.has('Production Date')"
                    v-validate="'required|date_format:yyyy-MM-dd'"
                    ref="Production Date"
                  />
                  <v-date-picker @change="productionDateMenu = false" v-model="updateForm.production_date" />
                </v-menu>
              </v-flex>

              <v-flex v-if="settings.product_expiry === 'Active'" xs4 sm4 class="pa-2">
                <v-menu v-model="productionExpireDateMenu" full-width max-width="290" :close-on-content-click="false">
                  <v-text-field
                    slot="activator"
                    :value="updateForm.production_expiration_date"
                    :label="$t('Production Expiration Date')"
                    prepend-inner-icon="event"
                    box
                    readonly
                    hide-details
                    background-color="white"
                    data-vv-name="Production Expiration Date"
                    autocomplete="off"
                    :error="errors.has('Production Expiration Date')"
                    v-validate="'date_format:yyyy-MM-dd|after:Production Date'"
                  />
                  <v-date-picker
                    @change="productionExpireDateMenu = false"
                    v-model="updateForm.production_expiration_date"
                  />
                </v-menu>
              </v-flex>

              <v-flex v-if="showMixtureDetail" xs12 style="margin-top: 40px; margin-bottom:20px">
                <v-layout row wrap justify-center mb-2>
                  <v-flex xs3 ml-5>
                    <h3>{{ $t('Mixture Details') }}</h3>
                  </v-flex>
                  <v-flex xs10 ml-5 mr-5>
                    <v-divider />
                  </v-flex>
                </v-layout>
                <v-layout row wrap class="view-table-header">
                  <v-flex xs1 pa-2>#</v-flex>
                  <v-flex xs5 pa-2>{{ $t('Raw Material') }}</v-flex>
                  <v-flex xs2 pa-2>{{ $t('Quantity') }}</v-flex>
                  <v-flex xs2 pa-2>{{ `${$t('Price')} (${systemCurrency})` }}</v-flex>
                  <v-flex xs2 pa-2>{{ `${$t('Total Cost')} (${systemCurrency})` }}</v-flex>
                </v-layout>

                <template v-if="mixtureDetail.length > 0">
                  <v-layout row wrap v-for="(row, index) in mixtureDetail" :key="index">
                    <v-flex class="view-table-row" xs1>
                      <p class="view-table-content">{{ index + 1 }}</p>
                    </v-flex>
                    <v-flex class="view-table-row" xs5>
                      <p class="view-table-content">
                        {{ row.raw_material_name }}
                      </p>
                    </v-flex>
                    <v-flex class="view-table-row" xs2>
                      <p class="view-table-content">
                        {{ row.mixture_quantity | currency }}
                      </p>
                    </v-flex>
                    <v-flex class="view-table-row" xs2>
                      <p class="view-table-content">
                        {{ row.product_average_price | currency }}
                      </p>
                    </v-flex>
                    <v-flex class="view-table-row" xs2>
                      <p class="view-table-content">
                        {{ row.cost | currency }}
                      </p>
                    </v-flex>
                  </v-layout>
                  <v-layout row wrap>
                    <v-flex xs12 style="text-align: right" pr-4 class="view-table-row">
                      <strong class="view-table-content"
                        >{{ $t('Total') }}: &nbsp; {{ mixtureTotalCost | currency }}</strong
                      >
                    </v-flex>
                  </v-layout>
                </template>
              </v-flex>
            </v-layout>
          </v-layout>
        </v-card-title>
        <v-divider light />

        <v-card-actions class="grey lighten-3">
          <v-layout column>
            <v-flex xs12 class="pb-2 px-2" style="color: #444"
              >- {{ $t('Fields marked with asterisk ( * ) are required.') }}</v-flex
            >

            <v-layout justify-end>
              <v-btn outline color="primary" @click="$emit('hidemodal')">{{ $t('Cancel') }}</v-btn>
              <v-btn type="submit" color="success" :loading="btnUpdateLoading">
                <v-icon :right="$vuetify.rtl" :left="!$vuetify.rtl">save</v-icon>
                {{ $t('Update') }}
              </v-btn>
            </v-layout>
          </v-layout>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as helpers from '@/mixins/helperFunctions.js'

export default {
  name: 'edit-component',

  props: {
    editDialog: { type: Boolean, required: true }
  },

  data() {
    return {
      btnUpdateLoading: false,
      showMixtureDetail: true,
      systemCurrency: localStorage.systemCurrency,
      productionDateMenu: false,
      productionExpireDateMenu: false,
      mixtureTotalCost: 0,
      updateForm: {
        staff_id_fk: '',
        product_id_fk: '',
        mixture_id_fk: '',
        production_quantity: '',
        used_mixture_quantity: '',
        shift_time: '',
        production_date: '',
        production_expiration_date: '',
        recycle_raw_material_id_fk: '',
        recycle_raw_material_quantity: ''
      },
      mixtureDetail: []
    }
  },

  computed: {
    ...mapGetters({
      fullStaffList: 'staff/getFullStaffsList',
      fullFactoriesList: 'factories/getFullFactoriesList',
      fullProductList: 'products/getFullProductsList',
      fullMixtureList: 'mixtures/getFullMixturesList',
      mixtureInfo: 'mixtures/getMixtureInfo',
      productionInfo: 'productions/getProductionInfo',
      settings: 'configurations/getSystemSettings',
      rawMaterialList: 'rawMaterials/getFullRawMaterialsList',
      shiftTimes: 'productions/getShiftTimes'
    }),
    estimatedQuantity() {
      const totalRawQuantity = this.mixtureInfo.mixture_total_quantity * this.updateForm.used_mixture_quantity
      const recycleQuantity = this.updateForm.recycle_raw_material_quantity || 0
      const total = (totalRawQuantity - recycleQuantity) / this.mixtureInfo.product_weight
      return isNaN(total) ? 0 : total.toFixed(2)
    },
    damagedQuantity() {
      const total = this.estimatedQuantity - this.updateForm.production_quantity
      return isNaN(total) || total < 0 ? 0 : total.toFixed(2)
    }
  },

  watch: {
    editDialog: {
      immediate: false,
      handler() {
        if (this.editDialog) {
          this.parseStaffStringToArray()
          this.mixtureTotalCost = this.productionInfo.all_mixtures_cost
          this.updateForm.factory_id_fk = this.productionInfo.factory_id_fk
          this.updateForm.product_id_fk = this.productionInfo.product_id_fk
          this.updateForm.mixture_id_fk = this.productionInfo.mixture_id_fk
          this.updateForm.production_quantity = this.productionInfo.production_quantity
          this.updateForm.used_mixture_quantity = this.productionInfo.used_mixture_quantity
          this.updateForm.shift_time = this.productionInfo.shift_time
          this.updateForm.production_date = this.productionInfo.production_date
          this.updateForm.production_expiration_date = this.productionInfo.production_expiration_date
          this.updateForm.recycle_raw_material_id_fk = this.productionInfo.recycle_raw_material_id_fk
          this.updateForm.recycle_raw_material_quantity = this.productionInfo.recycle_raw_material_quantity
          this.mixtureDetail = this.productionInfo.mixture_detail

          if (this.productionInfo !== null) {
            this.addEscKeyListener()
          }
        }
      }
    }
  },

  methods: {
    ...mapActions({
      updateProductionInfo: 'productions/updateProductionInfo',
      fetchMixtureInfo: 'mixtures/fetchMixtureInfo'
    }),

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    addEscKeyListener() {
      document.addEventListener('keyup', e => {
        // if the 'ESC' button was pressed and this dialog is shown, hide it
        if (e.keyCode === 27 && this.editDialog) {
          this.$emit('hidemodal')
        }
      })
    },

    parseStaffStringToArray() {
      this.updateForm.staff_id_fk = this.productionInfo.staff_id_fk.split(',').map(item => {
        return parseInt(item)
      })
    },

    getMixtureInfo(mixtureId) {
      this.fetchMixtureInfo(mixtureId).then(() => {
        this.mixtureDetail = this.mixtureInfo.raw_materials
        this.mixtureTotalCost = Number(this.mixtureInfo.mixture_total_cost)
        this.showMixtureDetail = true
      })
    },

    validateBeforeSubmit(event) {
      this.$validator.validateAll().then(() => {
        if (!this.errors.any()) {
          this.btnUpdateLoading = true
          let formData = new FormData(event.target)
          formData.append('factory_id_fk', this.updateForm.factory_id_fk)
          formData.append('product_id_fk', this.updateForm.product_id_fk)
          formData.append('mixture_id_fk', this.updateForm.mixture_id_fk)
          formData.append('recycle_raw_material_id_fk', this.updateForm.recycle_raw_material_id_fk || '')
          formData.append('shift_time', this.updateForm.shift_time)
          this.updateForm.staff_id_fk.forEach((p, index) => {
            formData.append('staff_id_fk[' + [index] + ']', p)
          })
          formData.append('production_date', this.updateForm.production_date)
          formData.append('production_expiration_date', this.updateForm.production_expiration_date || '')

          let payload = { id: this.productionInfo.production_id, formData }
          this.updateProductionInfo(payload)
            .then(() => {
              this.btnUpdateLoading = false
              this.$emit('updated')
            })
            .catch(() => {
              this.btnUpdateLoading = false
            })
        }
      })
    }
  }
}
</script>

<style scoped />
